// DEFECT 2 (her order, 2026-08-06) — **THE CREATOR NAMES HER GROUP AND GIVES IT A PICTURE.**
//
// The group image shipped with a route and no door: only the creator may set one, and nothing on the
// thread offered it. She has a group with her daughters and could not find where to rename it.
//
// **IT WORKS ON GROUPS CREATED BEFORE THIS SHIPPED**, and that is not a migration — `iCreated` is
// computed from `created_by`, which every thread in this room has carried since LR1. There is no
// "new group" state to be in.
//
// **BOTH WRITES GO THROUGH THE ROUTES THAT ALREADY REFUSE A NON-CREATOR** (`/api/room/state`'s
// sibling for the name, `/api/living-room/group-image` for the picture), so the permission is the
// server's in both cases and this sheet never decides anything it could get wrong.
import { useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { postJson } from "./LivingRoomShared";
import { useEscapeKey } from "./useEscapeKey";
import { prepareAvatarForUpload } from "../photoUpload.js";
import {
  GROUP_EDIT_LABEL, GROUP_NAME_LABEL, GROUP_PICTURE_LABEL,
  GROUP_DELETE_LABEL, groupDeleteConfirm,
  threadImageRefusal, photoTypeFor, UNREADABLE_PICTURE,
} from "../livingRoom.js";
import { nextTitle } from "./RenameControl";

export function GroupEditSheet({ thread, onClose, onSaved, onDeleted }: {
  thread: { id: string; title: string; image?: string | null };
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const [name, setName] = useState(thread.title || "");
  const [preview, setPreview] = useState<string | null>(thread.image || null);
  const [pending, setPending] = useState<{ data: string; contentType: string; width: number; height: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  // **THE ASK IS ITS OWN STATE, NEVER A `window.confirm`** — R35's law: this house asks before it
  // destroys, in its own words, on its own surface.
  const [asking, setAsking] = useState(false);
  useEscapeKey(onClose);

  const pick = async (file: File | null) => {
    if (!file) return;
    setNote("");
    // **THE HONESTY LAW: A FAILED PICK SAYS SO, IN PLAIN WORDS.** It used to leave a broken icon
    // and a submit that refused forever — which is what she reported. Every path below either
    // produces a picture she can see or a sentence she can read.
    // **THE SENTENCE IS THE SERVER'S OWN.** `threadImageRefusal` is what the route answers with, so
    // asking it here means she is refused at the picker in the SAME words rather than getting a
    // second opinion one screen earlier (R28/R29) — and a limit change moves both at once.
    // The NAME answers when the browser will not — an iPhone HEIC arrives with no type at all.
    const type = photoTypeFor(file.name, file.type);
    const refused = threadImageRefusal({ bytes: file.size, contentType: type });
    if (refused) { setNote(refused); return; }
    try {
      // **THE CROP IS ATTEMPTED FOR EVERY FORMAT, AND THAT IS THE ROUND'S ONE REAL DECISION.**
      //
      // It used to branch on a static list of what a canvas "can" decode and send everything else
      // whole for the server to convert. **MEASURED, THE SERVER CANNOT CONVERT A HEIC** — this
      // build's libheif reads the container and has no HEVC decoding plugin, so a real iPhone photo
      // came back `bad seek` / `No decoding plugin installed`. Bytes nobody can render are worse
      // than a refusal: they save clean and draw as a broken square on every screen in the group.
      //
      // **WHAT DOES WORK IS HER PHONE.** iOS Safari decodes HEIC natively, so the canvas that was
      // being skipped is exactly the thing that succeeds on the device the file comes from — and it
      // hands back an ordinary JPEG, so nothing downstream needs to know a HEIC was ever involved.
      // A browser that genuinely cannot decode it says so, in words, and never draws the broken icon.
      const ready = await prepareAvatarForUpload(file);
      if (!ready || !ready.base64) { setNote(UNREADABLE_PICTURE); return; }
      setPending({ data: ready.base64, contentType: ready.contentType, width: ready.width, height: ready.height });
      setPreview(ready.dataUrl);
    } catch {
      setNote(UNREADABLE_PICTURE);
    }
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setNote("");
    try {
      // **THE NAME ONLY IF IT REALLY CHANGED** — `nextTitle` is the shared law R38 already owns: a
      // blank name is not a rename and an unchanged one is not a request.
      const wanted = nextTitle(name, thread.title);
      if (wanted !== null) {
        const d = await postJson("/api/living-room/group-name", { threadId: thread.id, title: wanted });
        // R39/R47 — READ THE PAYLOAD. These routes answer 200 with `ok:false` on a refusal.
        if (!d?.ok) { setNote(d?.message || "That name did not save."); setBusy(false); return; }
      }
      if (pending) {
        const d = await postJson("/api/living-room/group-image", { threadId: thread.id, ...pending });
        if (!d?.ok) { setNote(d?.message || "That picture did not save."); setBusy(false); return; }
      }
      onSaved();
    } catch {
      setNote("That did not save. Try again in a moment.");
    }
    setBusy(false);
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setNote("");
    try {
      const d = await postJson("/api/living-room/group-delete", { threadId: thread.id });
      // R39/R47 — read the PAYLOAD. A refusal answers with `ok:false`, never a status code alone.
      if (d?.ok) onDeleted();
      else { setNote(d?.message || "That group could not be deleted."); setBusy(false); }
    } catch {
      setNote("That group could not be deleted.");
      setBusy(false);
    }
  };

  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="modal-backdrop lr-modal-backdrop" role="dialog" aria-modal="true"
      aria-label={GROUP_EDIT_LABEL} onMouseDown={onClose}>
      <div className="lr-modal lr-groupedit" onMouseDown={(e) => e.stopPropagation()}>
        <button className="lr-modal__close" type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <h2>{GROUP_EDIT_LABEL}</h2>

        <label className="lr-field">
          <span>{GROUP_NAME_LABEL}</span>
          <input className="lr-input" value={name} onChange={(e) => setName(e.target.value)}
            maxLength={60} aria-label={GROUP_NAME_LABEL} />
        </label>

        <div className="lr-field">
          <span>{GROUP_PICTURE_LABEL}</span>
          <div className="lr-groupedit__pic">
            {preview ? <img className="lr-groupedit__preview" src={preview} alt="" /> : <span className="lr-groupedit__none" aria-hidden="true" />}
            <label className="lr-groupedit__pick">
              Choose a picture
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
                onChange={(e) => { void pick(e.target.files?.[0] || null); }} />
            </label>
          </div>
        </div>

        {/* NEW RULING — **DELETE, AND THE CONFIRM SAYS WHAT REALLY HAPPENS.** Her words:
            destruction never dresses as exit, so the sentence names the group AND says it ends for
            EVERYONE, not just for her. It is DERIVED from the title (R35: never typed at the call
            site). Only the creator reaches this sheet, and the SERVER refuses anybody else anyway. */}
        <div className="lr-groupedit__danger">
          {asking ? (
            <>
              <p className="lr-groupedit__ask">{groupDeleteConfirm(thread.title)}</p>
              <div className="lr-confirm">
                <button type="button" className="button button--ghost" onClick={() => setAsking(false)}>Keep it</button>
                <button type="button" className="button button--danger" disabled={busy}
                  onClick={() => { void remove(); }}>{busy ? "Deleting..." : GROUP_DELETE_LABEL}</button>
              </div>
            </>
          ) : (
            <button type="button" className="lr-groupedit__delete" onClick={() => setAsking(true)}>
              {GROUP_DELETE_LABEL}
            </button>
          )}
        </div>

        {note ? <p className="lr-note">{note}</p> : null}
        <div className="lr-confirm">
          <button type="button" className="button button--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="lr-primary" onClick={() => { void save(); }} disabled={busy}>
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
